// @ts-nocheck
const BatchingPhase = {
  idle: null,
  queued: 'queued',
  inProgress: 'inprogress',
  done: 'done',
  failed: 'failed',
  blocked: 'blocked',
  locked: 'locked',
};

const BatchingPollErrorCodes = {
  UnsubscribeHandlerException: 0,
  VisitNotInBatchException: 1,
  VisitNotPollingException: 2,
};

const BatchingPollingIntensityMap = {
  idle: 7000,
  queued: 2000,
  inprogress: 300,
};

export default class Batching {
  constructor($http, $interval, Endpoints, Orders, SLVToggle, Auth, Polling, Utils) {
    Object.assign(this, {
      $http,
      $interval,
      Endpoints,
      Orders,
      SLVToggle,
      Auth,
      Utils,
      Polling,
      BatchingPhase,
      BatchingPollErrorCodes,
      subscriptions: new Map()
    });
  }

  transformVisitBatchStatus(visit) {
    const batch_status = visit.batchstatus ? visit.batchstatus.status : null;
    const lockedByMe = !!(visit.locked_by && visit.locked_by.id === this.Auth.profile.id);
    const lockedByAnother = !!(visit.locked_by && !lockedByMe);
    const isBatchable = visit.is_batchable
      && !visit.is_batch_blocked
      && (!visit.locked_by || lockedByMe)
      && batch_status === BatchingPhase.idle;
    return {
        status: visit.is_batch_blocked
          ? BatchingPhase.blocked
          : lockedByAnother
            ? BatchingPhase.locked
            : batch_status,
        shouldBatch: isBatchable,
        message: lockedByAnother
          ? `Locked by Dr. ${visit.locked_by.firstname[0]} ${visit.locked_by.lastname}`
          : batch_status,
    };
  }

  getVisits(pagination) {
    const query = this.Utils.serializeQuery(pagination)
      .replace('?', '');
    return this.$http.get(`${this.Endpoints.orders.base}?is_batchable=true&${query}`)
      .then(({ data }) => {
        const visits = data.data.map((visit) => {
          return {
            ...visit,
            batch: this.transformVisitBatchStatus(visit),
          };
        });
        return {
          visits,
          pagination: data.pagination,
        };
      });
  }

  getPollURL(visit) {
    return `${this.Endpoints.batching.batchStatus.replace(":id", visit.id)}?polling=true`;
  }

  sendForBatching(order_ids) {
    return this.$http.put(this.Endpoints.batching.scheduleVisits, { order_ids });
  }

  getBatchableHistory(visit) {
    return this.$http.get(this.Endpoints.batching.batchHistory.replace(":id", visit.id));
  }

  getCategories(preSelectedService) {
    const categories = [];
    return this.SLVToggle.getAllServices()
      .then(({ data: { categories: slvOptions } }) => {
        return this.Orders.getCategories()
          .then(({ data }) => {
            data.forEach((s) => {
              if (s.children && s.children.length) {
                s.children.forEach((c) => {
                  const option = slvOptions.find(service => service.category_code === c.code);
                  categories.push({
                    name: c.code + ' - ' + c.display_name,
                    category_code: c.code,
                    selected: preSelectedService === c.code,
                    batching_enabled: !!option.batching_enabled,
                  });
                });
              } else {
                const option = slvOptions.find(service => service.category_code === s.code);
                categories.push({
                  name: s.code + ' - ' + s.display_name,
                  category_code: s.code,
                  selected: preSelectedService === s.code,
                  batching_enabled: !!option.batching_enabled,
                });
              }
            });
            return categories;
          });
      });
  }

  getBatchRuleTypes() {
    return this.$http.get(this.Endpoints.batching.ruleTypes)
      .then(({data: ruleTypes}) => ruleTypes);
  }
  createBatchRule(rule) {
    return this.$http.post(this.Endpoints.batching.rules, rule);
  }
  getBatchRules() {
    return this.$http.get(this.Endpoints.batching.rules)
      .then(({ data: rules }) => rules);
  }
  updateBatchRule(rule) {
    return this.$http.put(
      this.Endpoints.batching.rule.replace(':id', rule.id),
      rule.editable
    );
  }
  deleteBatchRule(rule) {
    return this.$http.delete(this.Endpoints.batching.rule.replace(':id', rule.id));
  }

  blockVisitFromBatching(visit) {
    return this.$http.put(
      this.Endpoints.orders.detail.replace(":id", visit.id), {
        is_batch_blocked: true,
      }
    ).then(() => {
      if (visit.batch) {
        visit.batch.status = BatchingPhase.blocked;
      }
      visit.is_batch_blocked = true;
      this.stopPollingForBatchStatusUpdates(visit);
    });
  }

  processVisits(visits) {
    const visit_ids = visits.map(visit => visit.id);
    visits.forEach(
      visit => this.Polling.updatePollerOffset(
        this.getPollURL(visit),
        BatchingPollingIntensityMap.queued
      )
    );
    return this.sendForBatching(visit_ids);
  }

  isPollableVisit(status) {
    return [
      BatchingPhase.idle,
      BatchingPhase.queued,
      BatchingPhase.inProgress,
    ].includes(status);
  }

  getPollingIntensityInterval(status) {
    return status
      ? BatchingPollingIntensityMap[status]
      : BatchingPollingIntensityMap.idle;
  }

  startPollingForBatchStatusUpdates(visit) {
    if (!this.subscriptions.has(visit.id) && this.isPollableVisit(visit.batch.status)) {
      const subscription = {
        callbacks: {
          id: 0,
          functions: new Map(),
        },
      };
      this.subscriptions.set(visit.id, subscription);
      this.Polling.setupPoller(
        this.getPollURL(visit),
        this.getPollingIntensityInterval(visit.batch.status),
        (payload) => this.onBatchStatusUpdate(visit, payload)
      );
    }
  }

  onBatchStatusUpdate(visit, batchUpdate) {
    if (
      batchUpdate &&
      (visit.batch.status !== batchUpdate.status
        || visit.batch.message !== batchUpdate.message)
    ) {
      const subscription = this.subscriptions.get(visit.id);
      subscription.callbacks.functions.forEach(({ callback }) => {
        callback.call(this, visit, batchUpdate);
      });
      if (batchUpdate && !this.isPollableVisit(batchUpdate.status)) {
        this.stopPollingForBatchStatusUpdates(visit);
      } else if (this.isPollableVisit(batchUpdate.status)) {
        const newOffset = this.getPollingIntensityInterval(batchUpdate.status);
        this.Polling.updatePollerOffset(this.getPollURL(visit), newOffset);
      }
    }
  }

  stopPollingForBatchStatusUpdates(visit) {
    if (this.subscriptions.has(visit.id)) {
      this.subscriptions.get(visit.id).callbacks.functions
        .forEach(({autoUnsubscribe}) => {
          autoUnsubscribe(visit);
        });
    }
    this.Polling.destroyPoller(this.getPollURL(visit));
    this.subscriptions.delete(visit.id);
  }

  unsubscribeFromVisit(visit, callback_id) {
    const callbacks = this.subscriptions.get(visit.id).callbacks;
    callbacks.functions.delete(callback_id);

    if (!callbacks.functions.size) {
      this.stopPollingForBatchStatusUpdates(visit);
    }
  }

  subscribeToVisit(visit, callback, autoUnsubscribe) {
    return new Promise((resolve, reject) => {
      if (!autoUnsubscribe) {
        reject({
          error: 'visit must pass unsubscription handler',
          code: BatchingPollErrorCodes.UnsubscribeHandlerException,
        });
      }
      this.startPollingForBatchStatusUpdates(visit);
      if (this.subscriptions.has(visit.id)) {
        const callbacks = this.subscriptions.get(visit.id).callbacks;
        const callback_id = callbacks.id;
        callbacks.functions.set(callback_id, {
          callback,
          autoUnsubscribe,
        });
        resolve({
          unsubscribe: () => this.unsubscribeFromVisit(visit, callback_id),
        });
        callbacks.id += 1;
      }
    });
  }
}

Batching.$inject = ["$http", "$interval", "Endpoints", "Orders", "SLVToggle", "Auth", "Polling", "Utils"];
angular.module('pims').service('Batching', Batching);
